import React, { useMemo, useState } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { Category, CategoryKey } from '../../data/services';
import { colors, inputText, radii, screenPadding, spacing } from '../../theme';
import BottomSheet from '../ui/BottomSheet';
import Icon from '../ui/Icon';
import Text from '../ui/Text';
import CategoryGrid from './CategoryGrid';

/**
 * Every category, raised over Home.
 *
 * Home shows the first few; this is the rest. It is deliberately only a
 * picker - choosing one hands straight back to the same subcategory route a
 * tap on the home grid takes, so there is one path into a category rather than
 * two that drift apart.
 *
 * It used to list every category's services inline, first all expanded and
 * then as an accordion. Both were the wrong screen: the app already has a
 * subcategory sheet and a subcategory page, and a third way of browsing the
 * same catalogue only competed with them.
 */

type Props = {
  visible: boolean;
  onClose: () => void;
  categories: Category[];
  /** Where the work would happen, echoed from the home header. */
  address: string;
  onSelectCategory: (key: CategoryKey) => void;
};

function CatalogueSheet({
  visible,
  onClose,
  categories,
  address,
  onSelectCategory,
}: Props) {
  const [query, setQuery] = useState('');

  /*
   * Matched on the label only. The sheet lists categories, so searching their
   * services here would return rows that are not on screen to select - the
   * service-level search belongs to the search screen, not to a picker.
   */
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return categories;
    }
    return categories.filter(category =>
      category.label.toLowerCase().includes(needle),
    );
  }, [categories, query]);

  return (
    <BottomSheet visible={visible} onClose={onClose} heightRatio={0.86}>
      <View style={styles.header}>
        <View style={styles.place}>
          <Icon name="pin" size={18} color={colors.primary} />
          <View style={styles.placeText}>
            <Text variant="subtitle" numberOfLines={1}>
              All services
            </Text>
            <Text variant="caption" numberOfLines={1}>
              {address}
            </Text>
          </View>
        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close"
          onPress={onClose}
          hitSlop={12}
          style={({ pressed }) => [
            styles.close,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon
            name="plus"
            size={20}
            color={colors.ink}
            style={styles.closeGlyph}
          />
        </Pressable>
      </View>

      <View style={styles.search}>
        <Icon name="search" size={18} color={colors.inkMuted} />
        <TextInput
          value={query}
          onChangeText={setQuery}
          placeholder="Search categories"
          placeholderTextColor={colors.inkSubtle}
          style={styles.input}
          accessibilityLabel="Search categories"
          returnKeyType="search"
        />
        {query.length > 0 ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Clear search"
            onPress={() => setQuery('')}
            hitSlop={10}
          >
            <Icon
              name="plus"
              size={16}
              color={colors.inkMuted}
              strokeWidth={2.2}
              style={styles.closeGlyph}
            />
          </Pressable>
        ) : null}
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
        // Dismisses the keyboard when the list is dragged, so a tile can be
        // reached without a separate tap to close it first.
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
      >
        {shown.length > 0 ? (
          /*
            `square`, the same as Home's own grid - and for the same reason.
            -----------------------------------------------------------------
            Without it the grid draws its default: a CUTOUT standing on a
            plinth, sized and lifted so a figure with a transparent background
            escapes upward out of its card.

            A category's picture is not a cutout. It is a photograph from the
            console, with corners and a background of its own, so that mode
            drew a wide rectangle hovering over a pale green slab with the
            slab showing all round it - which reads as a picture that failed
            to load into its box.

            `square` says "framed photograph": the tile IS the frame, the
            image fills it, and its corners are rounded to match.
          */
          <CategoryGrid categories={shown} square onSelect={onSelectCategory} />
        ) : (
          <Text variant="bodySm" color={colors.inkMuted} align="center">
            Nothing matches "{query.trim()}".
          </Text>
        )}
      </ScrollView>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  place: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  placeText: {
    flex: 1,
  },
  close: {
    width: 34,
    height: 34,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* A rotated plus, so the set needs no separate close glyph. */
  closeGlyph: {
    transform: [{ rotate: '45deg' }],
  },
  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    height: 42,
    marginHorizontal: screenPadding,
    marginBottom: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surfaceMuted,
  },
  input: {
    flex: 1,
    ...inputText,
  },
  scroll: {
    paddingHorizontal: screenPadding,
    paddingBottom: spacing.xxl,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default CatalogueSheet;
